import { useEffect, useMemo, useState } from "react";
import {
  SettlementTournamentMember,
  SettlementTournamentDto,
  getSettlementTournament,
  SettlementTournament,
  EntryType,
} from "../../../../api/settlement";
import {
  ParticipantHistoryDto,
  getTournamentUserHistory,
  PaymentMethod,
  ParticipantPaymentResponse,
} from "../../../../api/tournament-participant";
import PopupPagination from "../../../../component/Pagination/popupPagination";
import {
  MainWrapper,
  MoreDataItem,
  MoreDataWrapper,
  MoreDateHeaderWrapper,
  PopupHeader,
  SolutionTournamentHeader,
  SolutionTournamentHeaderItem,
  SolutionTournamentHeaderItemWrapper,
  SolutionTournamentHeaderTotal,
  SolutionTournamentWrapper,
  TableDataWrapper,
  TableMoreWrapper,
  TableWrapper,
  TournamentTableHeader,
  TournamentTableListItemWrapper,
  TournamentTableListWrapper,
  TournamentTableMoreHeader,
  TournamentTableMoreHeaderItemWrapper,
} from "./style";
import { useRecoilValue } from "recoil";
import { pubState } from "../../../../recoil/pub.atom";
import { Title } from "../../../../styles/page";
import { exchangePoint } from "../../../../utils/number";

interface TournamentProps {
  tournament: SettlementTournament | null;
  onClose: () => void;
}

const TournamentPopup = ({ tournament, onClose }: TournamentProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [list, setList] = useState<SettlementTournamentDto[]>([]);
  const [userDataList, setUserDataList] = useState<ParticipantHistoryDto[]>([]);
  const [isSelectId, setIsSelectId] = useState<number>(-1);
  const pub = useRecoilValue(pubState);
  const [totalPrice, setTotalPrice] = useState(0);
  const [totalCash, setTotalCash] = useState(0);
  const [totalCard, setTotalCard] = useState(0);
  const [totalBank, setTotalBank] = useState(0);
  const [totalPoint, setTotalPoint] = useState(0);
  const [totalSeed, setTotalSeed] = useState(0);
  const [totalDiscount, setTotalDiscount] = useState(0);
  const limit = 8;

  const getData = async () => {
    if (!tournament) return;
    try {
      const res = await getSettlementTournament(tournament.tournamentId);

      setTotalPrice(res.totalPrice);
      setTotalCash(res.totalCash);
      setTotalCard(res.totalCard);
      setTotalBank(res.totalBank);
      setTotalPoint(res.totalPoint);
      setTotalSeed(res.totalSeed);
      setTotalDiscount(res.totalDiscount);
      setTotalPage(Math.max(1, Math.ceil(res.list.length / limit)));
      setList(res.list.slice((currentPage - 1) * limit, currentPage * limit));
    } catch (e: any) {
      console.log(e);
    }
  };

  const getUserData = async (memberId: number) => {
    if (!tournament) return;
    try {
      const res = await getTournamentUserHistory(
        memberId,
        tournament.tournamentId,
      );
      setIsSelectId(memberId);
      setUserDataList(res);
    } catch (e) {
      setUserDataList([]);
      console.log(e);
      return;
    }
  };

  const handlerPay = (item: SettlementTournamentMember[], type: string) => {
    if (!pub) return 0;
    const includedMethodsForAll = ["CARD", "CASH", "BANK_TRANSFER"];
    let result = 0;
    item.forEach((pay) => {
      pay.payment?.forEach((data) => {
        const isAllType =
          type === "ALL" && includedMethodsForAll.includes(data.paymentMethod);
        const isMatchedType = data.paymentMethod === type;

        if (isAllType || isMatchedType) {
          result += data.paymentAmount;
        }
      });
    });

    return type === "POINTS" ? exchangePoint(result) : result.toLocaleString();
  };

  const handlerEntry = (item: SettlementTournamentMember[], value: string) => {
    const result = item.reduce(
      (a, b) => a + (b.entryType === value ? 1 : 0),
      0,
    );
    return result;
  };

  const onPageChange = async (nextPage: number) => {
    setCurrentPage(nextPage);
  };

  useEffect(() => {
    getData();
  }, [currentPage]);

  const handleMoreClick = (memberId: number) => {
    if (isSelectId === memberId) {
      setIsSelectId(-1);
      return;
    }
    getUserData(memberId);
  };

  const handleLevelTime = (level: number, time: Date) => {
    const date = new Date(time);
    const hh = String(date.getHours()).padStart(2, "0");
    const mm = String(date.getMinutes()).padStart(2, "0");

    return `LV${level}(${hh}:${mm})`;
  };

  const formatEntryType = (entryType: string, isCount: boolean) => {
    if (entryType === "RE_BUY") {
      return "리엔트리";
    } else if (entryType === "BUY_IN") {
      return "엔트리";
    } else {
      return `애드온(${isCount ? "Y" : "N"})`;
    }
  };

  const handlePayType = (item: ParticipantPaymentResponse[]) => {
    const value = item
      .map((item) => formatPayType(item.paymentMethod))
      .join(" / ");

    if (value.includes("/")) return `복합 (${value})`;
    return value;
  };

  const handlePay = (item: ParticipantPaymentResponse[]) => {
    const value = item
      .map((item) => formatPay(item.amount, item.paymentMethod))
      .join(" / ");

    return value;
  };

  const formatPay = (value: number, type: string) => {
    if (!pub) return;
    if (type === "POINTS") {
      return exchangePoint(value) + "E";
    } else if (type === "SEED") {
      return `${value.toLocaleString()}RABBIT`;
    } else {
      return `${value.toLocaleString()}원`;
    }
  };

  const paymentLabelMap = new Map<PaymentMethod, string>([
    [PaymentMethod.CASH, "현금"],
    [PaymentMethod.CARD, "카드"],
    [PaymentMethod.BANK_TRANSFER, "계좌이체"],
    [PaymentMethod.DISCOUNT, "할인"],
    [PaymentMethod.SEED, "크레딧"],
  ]);

  const formatPayType = (value: PaymentMethod) => {
    if (!pub) return;
    return paymentLabelMap.get(value) ?? "매장 이용권";
  };

  const payments = useMemo(() => {
    if (!pub) return [];
    const base = [
      {
        name: "현금",
        type: "CASH",
        unit: "원",
        value: totalCash,
      },
      {
        name: "계좌이체",
        type: "BANK_TRANSFER",
        unit: "원",
        value: totalBank,
      },
      {
        name: "카드",
        type: "CARD",
        unit: "원",
        value: totalCard,
      },
      {
        name: "할인",
        type: "DISCOUNT",
        unit: "원",
        value: totalDiscount,
      },
      {
        name: "매장 이용권",
        type: "POINTS",
        unit: "E",
        value: exchangePoint(totalPoint),
      },
      {
        name: "크레딧",
        type: "SEED",
        unit: "RB",
        value: totalSeed,
      },
    ];
    return base;
  }, [
    pub,
    totalBank,
    totalCard,
    totalCash,
    totalDiscount,
    totalPoint,
    totalSeed,
  ]);

  const moreHeaders = [
    { name: "엔트리", type: EntryType.BUY_IN },
    { name: "리엔트리", type: EntryType.RE_BUY },
    { name: "애드온", type: EntryType.ADD_ON },
  ];

  const moreInfoHeaders = [
    {
      header: "레벨(시간)",
      render: (item: ParticipantHistoryDto) =>
        handleLevelTime(item.level, item.createdAt),
    },
    {
      header: "구분",
      render: (item: ParticipantHistoryDto) =>
        formatEntryType(item.entryType, item.isCount),
    },
    {
      header: "결제 수단",
      render: (item: ParticipantHistoryDto) =>
        handlePayType(item.participantPaymentMethod),
    },
    {
      header: "금액",
      render: (item: ParticipantHistoryDto) =>
        handlePay(item.participantPaymentMethod),
    },
  ];

  return (
    <>
      <SolutionTournamentWrapper>
        <PopupHeader>
          <Title>{tournament?.title}</Title>
          <div className="back" onClick={() => onClose()}>
            <img src="/image-web/solution/close.svg" alt="close" />
          </div>
        </PopupHeader>
        <MainWrapper>
          <SolutionTournamentHeader>
            <SolutionTournamentHeaderTotal>
              <div>총 결제</div>
              <div className="value">
                {totalPrice?.toLocaleString()}
                <span> 원</span>
              </div>
            </SolutionTournamentHeaderTotal>
            <SolutionTournamentHeaderItemWrapper>
              {payments.map((item, index) => (
                <SolutionTournamentHeaderItem key={index}>
                  <div className="name">{item.name}</div>
                  <div className="value">
                    {item.value?.toLocaleString()}
                    <span>{item.unit}</span>
                  </div>
                </SolutionTournamentHeaderItem>
              ))}
            </SolutionTournamentHeaderItemWrapper>
          </SolutionTournamentHeader>
          <TableWrapper>
            <TournamentTableHeader>
              <div className="item no">NO</div>
              <div className="item name">닉네임</div>
              {payments.map((header, idx) => (
                <div className="item value" key={idx}>
                  {header.name}
                </div>
              ))}
              <div className="item total">총 결제 금액</div>
              <div className="item no" />
            </TournamentTableHeader>
            <TournamentTableListWrapper>
              {list.map((item, index) => (
                <TournamentTableListItemWrapper key={index}>
                  <TableDataWrapper>
                    <div className="item no">
                      {(currentPage - 1) * 8 + (index + 1)}
                    </div>
                    <div className="item name">
                      <span>{item.nickname}</span>
                    </div>
                    {payments.map((header, idx) => (
                      <div className="item value" key={idx}>
                        {handlerPay(item.data, header.type)} {header.unit}
                      </div>
                    ))}
                    <div className="item total">
                      <span>{handlerPay(item.data, "ALL")}원</span>
                    </div>
                    <div
                      className="item no"
                      onClick={() => handleMoreClick(item.memberId)}
                    >
                      <img
                        src={`/image-web/solution/${
                          isSelectId === item.memberId ? "up" : "down"
                        }-arrow.png`}
                        alt="자세히 보기"
                      />
                    </div>
                  </TableDataWrapper>
                  {isSelectId === item.memberId && (
                    <TableMoreWrapper>
                      <TournamentTableMoreHeader>
                        <div className="title">자세히 보기</div>
                        <TournamentTableMoreHeaderItemWrapper>
                          {moreHeaders.map((header, i) => (
                            <div className="info" key={i}>
                              <div>{header.name}</div>
                              <div className="weight">
                                {handlerEntry(item.data, header.type)}회
                              </div>
                            </div>
                          ))}
                        </TournamentTableMoreHeaderItemWrapper>
                      </TournamentTableMoreHeader>
                      <MoreDateHeaderWrapper>
                        {moreInfoHeaders.map((header, h) => (
                          <div className="item" key={h}>
                            {header.header}
                          </div>
                        ))}
                      </MoreDateHeaderWrapper>
                      <MoreDataWrapper>
                        {userDataList.map((more, mIndex) => (
                          <MoreDataItem key={mIndex}>
                            {moreInfoHeaders.map((header, h) => (
                              <div className="item" key={h}>
                                {header.render(more)}
                              </div>
                            ))}
                          </MoreDataItem>
                        ))}
                      </MoreDataWrapper>
                    </TableMoreWrapper>
                  )}
                </TournamentTableListItemWrapper>
              ))}
            </TournamentTableListWrapper>
          </TableWrapper>
          {totalPage > 1 && (
            <PopupPagination
              currentPage={currentPage}
              totalPages={totalPage}
              onPageChange={onPageChange}
            />
          )}
        </MainWrapper>
      </SolutionTournamentWrapper>
    </>
  );
};

export default TournamentPopup;
